/* nv-fsbar-fix.js — NEVEROVER free-shipping-bar FX correction (WIP theme)
 *
 * BUG (assets/bundle.js → class FreeShippingBar):
 *   const thresholdRated = +threshold * +Shopify.currency.rate;
 * data-threshold and data-cart-total are ALREADY in the customer's
 * presentment currency, so multiplying threshold by Shopify.currency.rate
 * inflates the goal on every market whose currency != shop base (RON).
 * Result: a sub-threshold cart wrongly shows "Free shipping unlocked!" + full bar.
 *
 * FIX: re-read the SAME data-* attributes, recompute WITHOUT the FX multiply,
 * clamp the bar to 0–100%, and overwrite the SAME rendered DOM the component
 * uses (message node + progress-bar-dots CSS custom property), replicating the
 * original message formatting byte-for-byte. We only correct the rendered
 * output; we never re-run connectedCallback (it may bind listeners), and we
 * never throw.
 *
 * Original component internals being mirrored (verbatim from bundle.js):
 *   tag                : free-shipping-bar
 *   dataset keys       : threshold, cartTotal, currencyFormat, progressMessage, achievedMessage
 *   message node       : [data-free-shipping-bar-message]  -> replaceChildren(text)
 *   progress-bar node  : [data-free-shipping-progress-bar-dots]
 *                        -> setAttribute('style', '--gsc-freeshipping-bar-percent: {pct}%')
 *   money format       : currencyFormat.replace(/{{.+?}}/g, `${diff}`)
 *   progress message   : progressMessage.replace('[value]', m)
 */
(function () {
  'use strict';

  var TAG = 'free-shipping-bar';
  var MESSAGE_NODE_SELECTOR = '[data-free-shipping-bar-message]';
  var PROGRESS_BAR_DOTS = '[data-free-shipping-progress-bar-dots]';
  var GUARD = '__nvFsbarGuardUntil'; // timestamp: ignore observer hits until then

  function now() {
    try { return (window.performance && performance.now) ? performance.now() : Date.now(); }
    catch (e) { return Date.now(); }
  }
  // Mark an element so observer callbacks (which fire async, after our writes)
  // skip the DOM mutations we just made — avoids feedback loops.
  function suppress(el) {
    try { el[GUARD] = now() + 400; } catch (e) {}
  }
  function isSuppressed(el) {
    try { return el && typeof el[GUARD] === 'number' && now() < el[GUARD]; }
    catch (e) { return false; }
  }

  // Re-read dataset, recompute correctly (no Shopify.currency.rate), overwrite DOM.
  function correctOne(el) {
    try {
      if (!el || el.nodeType !== 1) return;
      var ds = el.dataset || {};
      var threshold = ds.threshold;
      var cartTotal = ds.cartTotal;
      var currencyFormat = ds.currencyFormat;
      var progressMessage = ds.progressMessage;
      var achievedMessage = ds.achievedMessage;

      // Without these we cannot compute anything meaningful; bail safely.
      if (threshold == null || cartTotal == null) return;

      var thr = +threshold;
      var ct = +cartTotal;
      if (!isFinite(thr) || !isFinite(ct) || thr <= 0) return;

      // Correct logic: NO * Shopify.currency.rate. Same shapes as the original.
      var diff = +(thr - ct).toFixed(2);
      var pct = Math.min(Math.max((ct / thr) * 100, 0), 100);

      var messageNode = el.querySelector(MESSAGE_NODE_SELECTOR);
      var dots = el.querySelector(PROGRESS_BAR_DOTS);

      // Guard our own DOM writes against the (async) observer callback.
      suppress(el);

      if (diff > 0) {
        // Replicate original money formatting exactly.
        var m = (currencyFormat || '').replace(/{{.+?}}/g, '' + diff);
        var text = (progressMessage || '').replace('[value]', m);
        if (messageNode) messageNode.replaceChildren(text);
        if (dots) dots.setAttribute('style', '--gsc-freeshipping-bar-percent: ' + pct + '%');
      } else {
        if (messageNode) messageNode.replaceChildren(achievedMessage != null ? achievedMessage : '');
        if (dots) dots.setAttribute('style', '--gsc-freeshipping-bar-percent: 100%');
      }
    } catch (e) {
      /* never throw */
    }
  }

  function correctAll(root) {
    try {
      var scope = root && root.querySelectorAll ? root : document;
      var nodes = scope.querySelectorAll(TAG);
      for (var i = 0; i < nodes.length; i++) correctOne(nodes[i]);
      // If root itself is a <free-shipping-bar>, handle it too.
      if (root && root.matches && root.matches(TAG)) correctOne(root);
    } catch (e) {
      /* never throw */
    }
  }

  // Run after the component's connectedCallback has executed. Using rAF +
  // microtask deferral keeps us behind the component's synchronous render.
  function correctSoon(root) {
    try {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(function () { correctAll(root); });
      }
      Promise.resolve().then(function () { correctAll(root); });
    } catch (e) {
      correctAll(root);
    }
  }

  function bind() {
    try {
      // Initial pass: handle the already-defined case and any present nodes.
      correctSoon(document);

      // Re-apply once the element is upgraded/defined.
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined(TAG).then(function () {
          correctSoon(document);
        }).catch(function () {});
      }

      // The cart drawer re-renders via AJAX, replacing/adding <free-shipping-bar>
      // nodes and rewriting their inner DOM. Watch for those and re-correct.
      if (window.MutationObserver && document.body) {
        var observer = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            var mut = mutations[i];

            // Ignore mutations we caused ourselves (loop guard).
            var t = mut.target;
            var owner = t && t.closest ? t.closest(TAG) : null;
            if (owner && isSuppressed(owner)) continue;

            // Newly added free-shipping-bar nodes (or subtrees containing them).
            if (mut.addedNodes && mut.addedNodes.length) {
              for (var j = 0; j < mut.addedNodes.length; j++) {
                var n = mut.addedNodes[j];
                if (n.nodeType !== 1) continue;
                if (n.matches && n.matches(TAG)) {
                  correctSoon(n);
                } else if (n.querySelector && n.querySelector(TAG)) {
                  correctSoon(n);
                }
              }
            }

            // Attribute/character or childList changes inside an existing bar
            // (e.g. the component re-rendering its message/bar after AJAX).
            if (owner && !isSuppressed(owner)) {
              correctSoon(owner);
            }
          }
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [
            'data-threshold',
            'data-cart-total',
            'data-currency-format',
            'data-progress-message',
            'data-achieved-message',
            'style'
          ]
        });
      }
    } catch (e) {
      /* never throw */
    }
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind, { once: true });
    } else {
      bind();
    }
  } catch (e) {
    /* never throw */
  }
})();
